/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useId } from 'react';
import { FormGroup, Label } from 'reactstrap';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { Option } from '@g17eco/molecules/select/types';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { MessageFormUserMin } from '../types';
import { getFullName } from '@utils/user';

export type UserSelectUser = MessageFormUserMin & { hint?: string };

interface UserSelectFieldProps {
  label?: string;
  onChange: (userIds: string[]) => void;
  userIds?: string[];
  users: UserSelectUser[];
}

const getOption = (user: UserSelectUser): Option<string> => {
  const name = getFullName(user);
  return {
    value: user._id,
    searchString: name,
    // Same inherited icon as InheritedIcon, pinned to the row end; nowrap would widen the fit-content menu
    label: user.hint ? (
      <div className='d-flex align-items-center'>
        <span className='flex-fill'>{name}</span>
        <SimpleTooltip text={user.hint} className='ms-2 flex-shrink-0'>
          <i className='fal fa-sitemap opacity-75' role='img' aria-label={user.hint} />
        </SimpleTooltip>
      </div>
    ) : (
      name
    ),
  };
};

export const UserSelectField = (props: UserSelectFieldProps) => {
  const { label = 'To', userIds, onChange, users } = props;
  const inputId = useId();

  return (
    <FormGroup>
      <Label for={inputId}>{label}</Label>
      <SelectFactory
        selectType={SelectTypes.MultipleSelect}
        inputId={inputId}
        className='dont_translate'
        placeholder={'Users'}
        options={users.map(getOption)}
        isFullWidthOptions
        noOptionsMessage='No users available for selection'
        values={userIds ?? []}
        onChange={(values: string[]) => onChange(values)}
      />
    </FormGroup>
  );
};
